// useSidebarCounts — live sidebar counts for the ClientHub shell (Epic 3 · S3.1).
//
// Calls fn_now, fn_work_list, fn_conversation_counts, and fn_get_my_clients (its
// row count is the Clients total) in one Promise.all to get the counts that
// appear on the sidebar badges. Polls every 60s (00_CONTRACT §13). Never reorders
// a list under someone's cursor — this hook only feeds badges, not list order.
//
// The conversation count uses fn_conversation_counts (added 2026-09-14 DB-side
// fix for the 57014 first-load timeout). It returns ONLY the three badge numbers
// — { channels, dms, open_threads } — scoped exactly like the lists beside them,
// instead of running the full fn_conversation_rooms scan and throwing the lists
// away. The badge shows open_threads alone — the same canonical number the
// Conversations page header renders ("N open", from fn_conversation_counts —
// perf memo §4.2; the old fn_conversation_rooms.counts block is unscoped).
// It must NOT sum channels + dms + open_threads: channels and dms count
// containers (rooms you can scroll), not open conversations, so summing them
// inflates the badge above the header number (the badge-vs-header mismatch).
// p_client_id is optional and scopes to one client, same as the rooms call; the
// sidebar passes no args for the global badge. See docs/CONVERSATION-ROOMS-FIRST-LOAD-TIMEOUT.md.
//
// All RPCs were confirmed to EXIST on the live Supabase on 2026-09-06
// (they raise P0001 "no current team member" with the anon key — they need a
// real JWT). See docs/CLIENTHUB-RPC-GAPS.md.
//
// Errors degrade to null counts (badge hidden), never to zero — a null count
// means "couldn't load", a zero count means "nothing needs you", and conflating
// them is a lie (00_CONTRACT §6: null ≠ empty).

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { dedupe, rpcKey } from "../api/rpcCache";

const POLL_MS = 60000;

// Each RPC returns a different shape. We extract just the count for the badge.
// fn_now            → { total, by_kind, items[] }   (total is the server aggregate)
// fn_work_list     → { total, items[] } | array     (defensive — shape may vary)
// fn_conversation_counts → { channels, dms, open_threads } | array
const extractCount = {
  now: (data) => {
    if (data == null) return null;
    if (typeof data === "object" && "total" in data) return data.total;
    if (Array.isArray(data)) return data.length;
    return null;
  },
  work: (data) => {
    if (data == null) return null;
    if (typeof data === "object" && "total" in data) return data.total;
    if (Array.isArray(data)) return data.length;
    return null;
  },
  conversations: (data) => {
    // fn_conversation_counts returns { channels, dms, open_threads }. The badge
    // shows open_threads — the open-conversations count — to match the
    // Conversations page header ("N open"). channels/dms count containers, not
    // open conversations, so they are NOT added (that was the badge>header bug).
    if (data == null) return null;
    if (Array.isArray(data)) return data.length;
    if (typeof data === "object") {
      const n = Number(data.open_threads);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  },
};

// Read one count RPC. Deduped in-flight by RPC name (the four sidebar counts
// have distinct names, so they never collapse each other) — but the count it
// SHARES with a page (fn_conversation_counts on the Conversations page) uses
// the same key and becomes ONE request on the mount burst (perf memo §2 rule 5).
const readRpc = (name) =>
  dedupe(rpcKey(name, undefined), async () => {
    const { data, error } = await supabase.rpc(name);
    if (error) return null; // degrade to null — never zero
    return data;
  });

// The Clients total is the caller's FULL client roster — fn_get_my_clients
// returns every client they can see (all clients for a platform admin). We count
// the returned rows rather than v_client_attention, which omits any client with
// no attention row (so it undercounts — 286 of 287, not the true total).
// Degrades to null on error — null means "couldn't load" (badge shows a spinner
// while loading, then hides), never a misleading zero.
const readClientsCount = async () => {
  const { data, error } = await supabase.rpc("fn_get_my_clients");
  if (error) return null;
  return Array.isArray(data) ? data.length : null;
};

export function useSidebarCounts() {
  const [counts, setCounts] = useState({ now: null, work: null, conversations: null, clients: null });
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const fetchCounts = useCallback(async () => {
    const [nowData, workData, convCounts, clientsCount] = await Promise.all([
      readRpc("fn_now"),
      readRpc("fn_work_list"),
      readRpc("fn_conversation_counts"),
      readClientsCount(),
    ]);
    setCounts({
      now: extractCount.now(nowData),
      work: extractCount.work(workData),
      conversations: extractCount.conversations(convCounts),
      clients: clientsCount,
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCounts();
    timerRef.current = setInterval(fetchCounts, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchCounts]);

  return { counts, loading, refresh: fetchCounts };
}
